import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Modal,
  Typography,
} from "@mui/material";
import { FaRegCalendarTimes } from "react-icons/fa";
import { ReactComponent as ClockIcon } from "shared/assets/svg/clock.svg";
import { DEFAULT_AVATAR } from "shared/config/properties";
import { useAuth } from "shared/providers/auth";
import { ScheduleLight } from "shared/types/Schedule";
import { ReturnButton } from "components/ReturnButton";

interface WaitingPatientsProps {
  open: boolean;
  onClose: () => void;
}

const getStatusLabel = (item: ScheduleLight): string => {
  if (item.inService === 1) return "Em atendimento";
  if (item.st_schedule === "T") return "Chegou atrasado";
  return "Chegou";
};

const getStatusColor = (item: ScheduleLight): string => {
  if (item.inService === 1) return "#1976d2";
  if (item.st_schedule === "T") return "#ed6c02";
  return "#44de00";
};

const WaitingPatients: React.FC<WaitingPatientsProps> = ({ open, onClose }) => {
  const { scheduleData } = useAuth();

  const currentScheduleId = React.useMemo(() => {
    try {
      const raw = localStorage.getItem("personAttendance");
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed?.id ?? null;
      }
    } catch {}
    return null;
  }, [open]);

  const waitingPatients = React.useMemo(() => {
    const patients: (ScheduleLight & { time: string })[] = [];

    scheduleData.forEach((slot) => {
      slot.data?.forEach((item) => {
        const isInService = item.inService === 1 || item.id === currentScheduleId;
        if ((item.st_schedule === "C" || item.st_schedule === "T") && !isInService) {
          patients.push({ ...item, time: slot.time });
        }
      });
    });

    return patients.sort((a, b) => {
      if (a.preferencial !== b.preferencial) return b.preferencial - a.preferencial;
      return 0;
    });
  }, [scheduleData, currentScheduleId]);

  const waitingCount = waitingPatients.length;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          backgroundColor: "white",
          height: "100%",
          width: "100%",
          overflow: "auto",
        }}
      >
        <Box sx={{ padding: "16px" }}>
          <ReturnButton title="Pacientes Aguardando" onClick={onClose} />

          <Box
            sx={{
              display: "flex",
              gap: "12px",
              mb: 2,
              mt: 1,
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={`${waitingCount} aguardando`}
              sx={{
                backgroundColor: "#e8f5e9",
                color: "#2e7d32",
                fontWeight: 600,
                fontSize: "14px",
              }}
            />
          </Box>

          {waitingPatients.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                marginTop: "40px",
                gap: "8px",
              }}
            >
              <FaRegCalendarTimes size={32} color="#999" />
              <Typography sx={{ fontWeight: "bold", color: "#666" }}>
                Nenhum paciente aguardando
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {waitingPatients.map((patient) => (
                <Box
                  key={patient.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 16px",
                    backgroundColor: "#fff",
                    borderRadius: "10px",
                    border: patient.preferencial === 1
                      ? "2px solid #1976d2"
                      : "1px solid #e0e0e0",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  }}
                >
                  <Avatar
                    src={patient.pac_avatar || DEFAULT_AVATAR}
                    alt={patient.pac_name}
                    sx={{ width: 48, height: 48 }}
                  />

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      {patient.preferencial === 1 && (
                        <Typography
                          sx={{
                            color: "#1976d2",
                            fontWeight: 700,
                            fontSize: "11px",
                            textTransform: "uppercase",
                          }}
                        >
                          Preferencial
                        </Typography>
                      )}
                      <Typography sx={{ color: "#919296", fontSize: "13px" }}>
                        ID {patient.pac_id_fant}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        color: "#343536",
                        fontWeight: 700,
                        fontSize: "15px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {patient.pac_name}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#556370",
                        fontSize: "13px",
                        backgroundColor: "#eeeff1",
                        display: "inline-block",
                        padding: "1px 6px",
                        borderRadius: "4px",
                        mt: "2px",
                      }}
                    >
                      {patient.proc_name}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: "4px",
                      minWidth: "80px",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#007ded",
                        fontWeight: 700,
                        fontSize: "14px",
                      }}
                    >
                      {patient.time}
                    </Typography>

                    {patient.dt_arrival && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                        <ClockIcon width="14px" height="14px" />
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: patient.isAtrasado === 0 ? "rgb(68, 222, 0)" : "#ff5722",
                          }}
                        >
                          {patient.dt_arrival.slice(0, 5)}
                        </Typography>
                      </Box>
                    )}

                    <Chip
                      label={getStatusLabel(patient)}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(patient),
                        color: "white",
                        fontWeight: 700,
                        fontSize: "10px",
                        height: "20px",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export { WaitingPatients };
